import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import useIsLoading from "../../../../hooks/useIsLoading";
import { Talent } from "../../../talents/talentAPI";
import {
  addTalentTraining,
  selectTalentById,
} from "../../../talents/talentsSlice";
import TrainingsForm from "./TrainingsForm";
import { UnsavedChangesProvider } from "../../../../contexts/UnsavedChangesContext";

type Props = {
  onClose: () => void;
};

type TrainingFormValues = {
  type: string;
  completed: boolean;
  completedAt?: string | null;
  validTo?: string | null;
};

const TalentAddTraining = ({ onClose }: Props) => {
  const { t } = useTranslation();

  const { talentId } = useParams();
  const isLoading = useIsLoading(["talents"]);
  const dispatch = useAppDispatch();

  const talent = useAppSelector((state) =>
    selectTalentById(state, talentId!!),
  ) as Talent;

  const existingTrainingTypes =
    talent?.trainings?.map((training) => training.type) || [];

  const defaultValues: TrainingFormValues = {
    type: "",
    completed: false,
    completedAt: null,
    validTo: null,
  };

  const methods = useForm({ mode: "onChange", defaultValues });

  const formatDateString = (date?: string | Date) => {
    if (!date) return null;

    if (date instanceof Date) {
      return format(date, "yyyy-MM-dd");
    }

    const formattedDate = format(new Date(date), "yyyy-MM-dd");
    return formattedDate;
  };

  const onSave = (data: TrainingFormValues) => {
    // FIXME: create common utility function
    const parsedCompletedAt =
      data.completedAt && formatDateString(data.completedAt);
    const parsedValidTo = data.validTo && formatDateString(data.validTo);

    if (talentId) {
      const commandPayload = {
        talentId,
        type: data.type,
        completed: data.completed,
        ...(data.completed &&
          parsedCompletedAt && { completedAt: parsedCompletedAt }),
        ...(parsedValidTo && { validTo: parsedValidTo }),
      };

      dispatch(addTalentTraining(commandPayload)).then((action) => {
        if (isFulfilled(action)) {
          onClose();
        }
        return isFulfilled(action);
      });
    }
  };

  const onCancel = () => {
    onClose();
  };

  const disabled =
    !methods.formState.isDirty ||
    !!Object.keys(methods.formState.errors).length ||
    isLoading ||
    !methods.watch("type");

  return (
    <FormProvider {...methods}>
      <DialogContentWrapper
        title={t("trainings:training")}
        margin
        onSave={methods.handleSubmit(onSave)}
        onCancel={onCancel}
        saveDisabled={disabled}
      >
        <OverlaySpinner isLoading={isLoading} />
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <TrainingsForm existingTrainingTypes={existingTrainingTypes} />
        </UnsavedChangesProvider>
      </DialogContentWrapper>
    </FormProvider>
  );
};

export default TalentAddTraining;
